import axios, {
    CancelTokenSource,
    AxiosResponse,
    AxiosRequestConfig,
    ResponseType as AxiosResponseType,
} from 'axios';

import { acquireTokenWrapper } from '@/tools/session';

export const disableInterceptorKey: string = 'AXIOS-DISABLE-INTERCEPTOR';

async function interceptor(config: AxiosRequestConfig) {
    // Interceptor is enabled for all requests
    // It can be disabled using specific key in request headers
    // hack for public endpoints - maintain urls instead ?
    let enabled = true;

    // Check headers for interceptor key
    if (
        config.headers &&
        typeof config.headers[disableInterceptorKey] !== 'undefined'
    ) {
        const disable = config.headers[disableInterceptorKey];
        if (typeof disable === 'boolean' && disable === true) {
            enabled = false;
        }
        // Remove key from headers
        delete config.headers[disableInterceptorKey];
    }

    if (enabled) {
        if (config.url?.startsWith('http')) {
            // Valid token needed otherwise authentication will be required
            const token = await acquireTokenWrapper();
            config.headers = {
                ...config.headers,
                Authorization: 'Bearer ' + token,
            };
        }
    }
    return config;
}

async function callWithParameter<P = null>(
    url: string,
    source: CancelTokenSource | undefined,
    type: keyof IParameteredAjaxCallType,
    headers: Record<string, any> | null,
    parameter: P | null = null,
    responseType?: AxiosResponseType
): Promise<AxiosResponse<any>> {
    const config: AxiosRequestConfig = {
        cancelToken: source != null ? source.token : undefined,
        headers,
        responseType,
    };
    return axios[type](url, parameter, config);
}

async function callWithoutParameter(
    url: string,
    source: CancelTokenSource | undefined,
    type: keyof IAjaxCallType,
    headers: Record<string, any> | null,
    responseType?: AxiosResponseType
): Promise<AxiosResponse<any>> {
    const config: AxiosRequestConfig = {
        cancelToken: source != null ? source.token : undefined,
        headers,
        responseType,
    };
    return axios[type](url, config);
}

export function initWebStore() {
    axios.interceptors.request.use(interceptor);
}

export interface IApiError {
    type?: string;
    title?: string;
    status?: number;
    errors: { Code: string[] };
}

interface ICancellableResult<R> {
    cancelled: boolean;
    error?: IApiError;
    datas: R | null;
}
export interface IParameteredAjaxCallType {
    post: string;
    put: string;
    patch: string;
}
export interface IAjaxCallType {
    get: string;
    delete: string;
}
const cancellableObject: { [key: string]: CancelTokenSource } = {};
async function cancelGenericPreviousRequest(url: string) {
    if (url != null) {
        const tokenHandler = cancellableObject[url];
        if (tokenHandler != null) {
            tokenHandler.cancel('Cancelled');
        }
    }
}
async function addNewGenericCancellableToken(
    url: string,
    tokenSource: CancelTokenSource
) {
    cancellableObject[url] = tokenSource;
}
/**
 * Async Cancellable Method to allow auto cancellation when calling the same url multiple time.
 * @typeparam R         return type
 * @typeparam P         type object for parameters
 * @param url           url called
 * @param returnMethod  method that will get and modify the value
 * @param headers       headers for the call
 * @param parameter     parameter passed to the method
 * @returns             an ICancellableResult containing informations if it has been cancelled and the data associated.
 */
export default async function baseAjaxCall<R, P = null>(
    type: keyof IAjaxCallType | keyof IParameteredAjaxCallType = 'post',
    url: string,
    returnMethod: (val: any) => R,
    headers: Record<string, any> | null = null,
    parameter: P | null = null,
    canBeCancelled = false,
    responseType?: AxiosResponseType
): Promise<ICancellableResult<R>> {
    let source: CancelTokenSource | undefined;
    if (canBeCancelled) {
        await cancelGenericPreviousRequest(url);
        const CancelToken = axios.CancelToken;
        source = CancelToken.source();
        await addNewGenericCancellableToken(url, source);
    }
    const returnValue = {
        cancelled: false,
        datas: null,
    } as ICancellableResult<R>;
    try {
        let response = null;
        switch (type) {
            case 'get':
            case 'delete':
                response = await callWithoutParameter(
                    url,
                    source,
                    type,
                    headers,
                    responseType
                ).catch((error) => {
                    throw error;
                });
                break;
            case 'post':
            case 'put':
            case 'patch':
                response = await callWithParameter<P>(
                    url,
                    source,
                    type,
                    headers,
                    parameter,
                    responseType
                ).catch((error) => {
                    throw error;
                });
                break;
        }
        if (response && response.data) {
            const result = response.data;
            returnValue.datas = returnMethod(result);
        }
    } catch (error) {
        if (error.message === 'Cancelled') {
            returnValue.cancelled = true;
        } else if (error.response && error.response.status === 599) {
            // console.log('Please reload your page.', 'Session Timeout');
        } else if (error.response && error.response.data) {
            returnValue.error = error.response.data;
        }
    }
    return returnValue;
}
